import {
  annotationToolApiV1,
  getAnnotationToolBasePath,
  type ListResponse,
  type ObjectResponse,
  type RootState,
} from '@verantos-dev/core-annotation-tool';
import type { AnnotationTaskResponse } from '@verantos-dev/feature-annotator-queue';

import { ANNOTATION_TAG } from '../constants';
import type {
  AddSpanRequest,
  BulkCreateAnnotationsRequest,
  CreateAnnotationRequest,
  DeleteAnnotationRequest,
  FinalizeAnnotationRequest,
  UpdateAnnotationAttributesRequest,
} from '../types/request';
import type {
  AnnotationMutationResponse,
  AnnotationWorkspaceResponse,
  BulkAnnotationMutationResponse,
  DeleteAnnotationWithTaskStatusResponse,
  Note,
} from '../types/response';

export const annotationApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [ANNOTATION_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getEncounterNotes: builder.query<
        ListResponse<Note>,
        {
          taskId: string;
        }
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/encounters/${taskId}/notes`;
        },
        providesTags: [ANNOTATION_TAG],
      }),

      getEncounterDetails: builder.query<
        ObjectResponse<AnnotationTaskResponse>,
        {
          taskId: string;
        }
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/encounters/${taskId}`;
        },
        providesTags: [ANNOTATION_TAG],
      }),

      getAnnotationWorkspace: builder.query<
        ObjectResponse<AnnotationWorkspaceResponse>,
        {
          taskId: string;
        }
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/work-queue/tasks/${taskId}/annotation-workspace`;
        },
        providesTags: [ANNOTATION_TAG],
      }),

      addAnnotationSpans: builder.mutation<
        ObjectResponse<AnnotationMutationResponse>,
        AddSpanRequest
      >({
        query: ({ annotationId, spans }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/annotations/${annotationId}/spans`,
            method: 'POST',
            body: {
              spans,
            },
          };
        },
        async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
          const taskId = (getState() as RootState).annotation?.taskId ?? '';
          if (!taskId) return;
          try {
            const { data } = await queryFulfilled;
            const { taskStatus, ...updatedAnnotation } = data.data;
            dispatch(
              annotationApiSlice.util.updateQueryData(
                'getAnnotationWorkspace',
                { taskId },
                (draft) => {
                  const idx = draft.data.annotations.findIndex(
                    (a) => a.annotationId === updatedAnnotation.annotationId
                  );
                  if (idx !== -1) draft.data.annotations[idx] = updatedAnnotation;
                  if (taskStatus) draft.data.task.status = taskStatus;
                }
              )
            );
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      createAnnotation: builder.mutation<
        ObjectResponse<AnnotationMutationResponse>,
        CreateAnnotationRequest
      >({
        query: ({ taskId, conceptId, attributes, spans }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${taskId}/annotations`,
            method: 'POST',
            body: {
              conceptId,
              attributes,
              spans: spans || [],
            },
          };
        },
        async onQueryStarted({ taskId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const { taskStatus, ...newAnnotation } = data.data;
            dispatch(
              annotationApiSlice.util.updateQueryData(
                'getAnnotationWorkspace',
                { taskId },
                (draft) => {
                  draft.data.annotations.push(newAnnotation);
                  if (taskStatus) draft.data.task.status = taskStatus;
                }
              )
            );
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      updateAnnotationAttributes: builder.mutation<
        ObjectResponse<AnnotationMutationResponse>,
        UpdateAnnotationAttributesRequest
      >({
        query: ({ annotationId, attributes, spans, evidenceIdsToDelete }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/annotations/${annotationId}/attributes`,
            method: 'PUT',
            body: {
              attributes,
              spans: spans || [],
              evidenceIdsToDelete,
            },
          };
        },
        async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
          const taskId = (getState() as RootState).annotation?.taskId ?? '';
          if (!taskId) return;
          try {
            const { data } = await queryFulfilled;
            const { taskStatus, ...updatedAnnotation } = data.data;
            dispatch(
              annotationApiSlice.util.updateQueryData(
                'getAnnotationWorkspace',
                { taskId },
                (draft) => {
                  const idx = draft.data.annotations.findIndex(
                    (a) => a.annotationId === updatedAnnotation.annotationId
                  );
                  if (idx !== -1) draft.data.annotations[idx] = updatedAnnotation;
                  if (taskStatus) draft.data.task.status = taskStatus;
                }
              )
            );
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      deleteAnnotation: builder.mutation<
        ObjectResponse<DeleteAnnotationWithTaskStatusResponse>,
        DeleteAnnotationRequest
      >({
        query: ({ annotationId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/annotations/${annotationId}`,
            method: 'DELETE',
          };
        },
        async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
          const taskId = (getState() as RootState).annotation?.taskId ?? '';
          if (!taskId) return;
          try {
            const { data } = await queryFulfilled;
            dispatch(
              annotationApiSlice.util.updateQueryData(
                'getAnnotationWorkspace',
                { taskId },
                (draft) => {
                  draft.data.annotations = draft.data.annotations.filter(
                    (a) => a.annotationId !== data.data.annotationId
                  );
                  if (data.data.taskStatus) draft.data.task.status = data.data.taskStatus;
                }
              )
            );
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      bulkCreateAnnotations: builder.mutation<
        ObjectResponse<BulkAnnotationMutationResponse>,
        BulkCreateAnnotationsRequest
      >({
        query: ({ taskId, annotations }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${taskId}/annotations/bulk`,
            method: 'POST',
            body: {
              annotations,
            },
          };
        },
        async onQueryStarted({ taskId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const { annotations: newAnnotations, taskStatus } = data.data;
            dispatch(
              annotationApiSlice.util.updateQueryData(
                'getAnnotationWorkspace',
                { taskId },
                (draft) => {
                  draft.data.annotations.push(...newAnnotations);
                  if (taskStatus) draft.data.task.status = taskStatus;
                }
              )
            );
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      finalizeAnnotation: builder.mutation<
        ObjectResponse<AnnotationTaskResponse>,
        FinalizeAnnotationRequest
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${taskId}/finalize`,
            method: 'POST',
          };
        },
        invalidatesTags: (result, error) => (error ? [] : [ANNOTATION_TAG]),
      }),
    }),
  });

export const {
  useGetEncounterNotesQuery,
  useGetEncounterDetailsQuery,
  useGetAnnotationWorkspaceQuery,
  useAddAnnotationSpansMutation,
  useCreateAnnotationMutation,
  useUpdateAnnotationAttributesMutation,
  useDeleteAnnotationMutation,
  useBulkCreateAnnotationsMutation,
  useFinalizeAnnotationMutation,
} = annotationApiSlice;
